import {
  CreateCompositeShipmentRequest,
  CustomBookingsMethodTestRun,
  StepActionLog,
  StepFilterLog,
  StepLog as StepLogType,
} from '~api/som';

import { Fragment, useMemo, useState } from 'react';

import { ChevronDown16, ChevronRight16 } from '@ingrid/iconography/react';
import { Banner, DotBadge, ListLayout, PileBadge, Spinner, styled } from '@ingrid/luterkort-react';

import { SimpleSelect } from '~src/components/SimpleSelect/SimpleSelect';
import { useTranslation } from '~src/i18n/use-translation';
import { toPrettyJson } from '~src/utils/json';

import { useConfiguratorContext } from './configurator-context';
import { DiffEditor } from './diff-editor';
import { RunTestButton } from './run-test-button';
import { Footer, Wrapper } from './test-runner-layout';

const InteractiveGroup = styled('button', {
  background: 'transparent',
  userSelect: 'text',
  cursor: 'pointer',
  padding: 0,
  border: 0,
});

const ErrorText = styled(ListLayout.PrimaryText, {
  fontFamily: 'monospace',
  whiteSpace: 'pre-wrap',
  color: '#C80707',

  variants: {
    mode: {
      dark: {
        color: '#FF7272',
      },
    },
  },
});

function calculateDiffHeight(
  input: CreateCompositeShipmentRequest | undefined,
  output: CreateCompositeShipmentRequest | undefined
) {
  const heightPerLine = 18;
  const minHeight = heightPerLine * 2;
  const maxHeight = 200;

  const inputJson = toPrettyJson(input) ?? '';
  const outputJson = toPrettyJson(output ?? {}) ?? '';

  const inputLines = inputJson.split('\n').length;
  const outputLines = outputJson.split('\n').length;

  const diffLines = Math.max(inputLines, outputLines);

  return Math.min(Math.max(diffLines * heightPerLine, minHeight), maxHeight);
}

function RowBadge({ error, applied }: { error: string | undefined; applied: boolean | undefined }) {
  const { t } = useTranslation();

  const variant = error ? 'critical' : applied ? 'success' : ('neutral' as const);

  return (
    <PileBadge variant={variant}>
      {error ? t('common.failed') : applied ? t('common.passed') : t('common.skipped')}
    </PileBadge>
  );
}

function StepFilterStack({ filter }: { filter: StepFilterLog }) {
  const [isOpen, setIsOpen] = useState(!!filter.raw_error);
  const canToggle = !!filter.raw_error;

  const { t } = useTranslation();

  function handleToggle() {
    if (!canToggle) {
      return;
    }

    setIsOpen(p => !p);
  }

  return (
    <ListLayout.Stack style={{ marginLeft: '16px' }}>
      <InteractiveGroup onClick={handleToggle}>
        <ListLayout.Row style={{ flexWrap: 'nowrap', columnGap: '4px', alignItems: 'center', minWidth: 0 }}>
          <ToggleIcon isOpen={isOpen} disabled={!canToggle} />
          <RowBadge applied={filter.applied} error={filter.raw_error} />
          <ListLayout.SecondaryText style={{ flexShrink: 0, marginLeft: '4px' }}>
            {t('common.condition')}
          </ListLayout.SecondaryText>
          <ListLayout.PrimaryText style={{ textOverflow: 'ellipsis', textWrap: 'nowrap', overflow: 'hidden' }}>
            {filter.expression}
          </ListLayout.PrimaryText>
        </ListLayout.Row>
      </InteractiveGroup>

      {isOpen && filter.raw_error && (
        <ListLayout.Row>
          <ErrorText style={{ marginLeft: '84px' }}>{filter.raw_error}</ErrorText>
        </ListLayout.Row>
      )}
    </ListLayout.Stack>
  );
}

function StepActionStack({ action }: { action: StepActionLog }) {
  const [isOpen, setIsOpen] = useState(!!action.raw_error);
  const canToggle = (!!action.raw_error || action.input) ?? action.output;

  const { t } = useTranslation();

  function handleToggle() {
    if (!canToggle) {
      return;
    }

    setIsOpen(p => !p);
  }

  return (
    <ListLayout.Stack style={{ marginLeft: '16px' }}>
      <InteractiveGroup onClick={handleToggle}>
        <ListLayout.Row style={{ flexWrap: 'nowrap', columnGap: '4px', alignItems: 'center', minWidth: 0 }}>
          <ToggleIcon isOpen={isOpen} disabled={!canToggle} />
          <RowBadge applied={action.applied} error={action.raw_error} />
          <ListLayout.SecondaryText style={{ flexShrink: 0, marginLeft: '4px' }}>
            {t('common.action')}
          </ListLayout.SecondaryText>
          <ListLayout.PrimaryText style={{ textOverflow: 'ellipsis', textWrap: 'nowrap', overflow: 'hidden' }}>
            {action.expression}
          </ListLayout.PrimaryText>
        </ListLayout.Row>
      </InteractiveGroup>

      {isOpen && (
        <Fragment>
          {action.raw_error && (
            <ListLayout.Row>
              <ErrorText style={{ marginLeft: '84px' }}>{action.raw_error}</ErrorText>
            </ListLayout.Row>
          )}

          {(action.input ?? action.output) && (
            <ListLayout.Row style={{ height: `${calculateDiffHeight(action.input, action.output)}px` }}>
              <DiffEditor modified={toPrettyJson(action.output)} original={toPrettyJson(action.input)} />
            </ListLayout.Row>
          )}
        </Fragment>
      )}
    </ListLayout.Stack>
  );
}

function ToggleIcon({ isOpen, disabled }: { isOpen: boolean; disabled?: boolean }) {
  const IconComponent = isOpen ? ChevronDown16 : ChevronRight16;

  return (
    <IconComponent
      style={{ marginRight: '4px', width: '14px', height: '14px', flexShrink: 0, opacity: !disabled ? '1' : '.5' }}
    />
  );
}

function Step({ step }: { step: StepLogType }) {
  const [isOpen, setIsOpen] = useState(true);

  const hasSomeFailedFilter = step.filters?.some(f => f.raw_error);
  const hasSomeFailedAction = step.actions?.some(a => a.raw_error);
  const hasSomeFailed = hasSomeFailedFilter ?? hasSomeFailedAction;

  const hasSomeSkippedFilter = step.filters?.some(f => !f.applied);
  const hasSomeSkippedAction = step.actions?.some(a => !a.applied);
  const hasSomeSkipped = hasSomeSkippedFilter ?? hasSomeSkippedAction;

  const { t } = useTranslation();

  return (
    <Fragment>
      <InteractiveGroup onClick={() => setIsOpen(p => !p)}>
        <ListLayout.Row
          style={{
            alignItems: 'center',
            flexWrap: 'nowrap',
            columnGap: '4px',
            minWidth: 0,
          }}
        >
          <ToggleIcon isOpen={isOpen} />
          <PileBadge variant={hasSomeFailed ? 'critical' : hasSomeSkipped ? 'neutral' : 'success'}>
            {hasSomeFailed ? t('common.failed') : hasSomeSkipped ? t('common.skipped') : t('common.passed')}
          </PileBadge>
          <ListLayout.SecondaryText style={{ flexShrink: 0, marginLeft: '4px' }}>Step</ListLayout.SecondaryText>
          <ListLayout.PrimaryText style={{ textOverflow: 'ellipsis', textWrap: 'nowrap', overflow: 'hidden' }}>
            {step.name}
          </ListLayout.PrimaryText>
        </ListLayout.Row>
      </InteractiveGroup>

      {isOpen && (
        <Fragment>
          {step.filters?.map((filter, fi) => (
            <StepFilterStack filter={filter} key={`filter-${fi}`} />
          ))}
          {step.actions?.map((action, ai) => (
            <StepActionStack action={action} key={`action-${ai}`} />
          ))}
        </Fragment>
      )}
    </Fragment>
  );
}

export function TestRunnerOutput() {
  const { runs, isRunning } = useConfiguratorContext();

  const [selected, setSelected] = useState<string>('0');

  const result = useMemo<CustomBookingsMethodTestRun | undefined>(() => runs[Number(selected)], [runs, selected]);
  const steps = useMemo(() => result?.run_logs?.steps ?? [], [result]);
  const error = useMemo(() => result?.errors, [result]);

  const { t } = useTranslation();

  let numberOfSkipped = 0;
  let numberOfPassed = 0;
  let numberOfFailed = 0;

  for (const step of steps) {
    const filters = step.filters ?? [];
    const actions = step.actions ?? [];

    if (filters.some(f => f.raw_error) || actions.some(a => a.raw_error)) {
      numberOfFailed++;
    }

    if (filters.some(f => !f.applied) && actions.some(a => !a.applied)) {
      numberOfSkipped++;
    }

    if (filters.every(f => f.applied) && actions.every(a => a.applied)) {
      numberOfPassed++;
    }

    for (const filter of filters) {
      if (filter.raw_error) {
        numberOfFailed++;
      } else if (filter.applied) {
        numberOfPassed++;
      } else {
        numberOfSkipped++;
      }
    }

    for (const action of actions) {
      if (action.raw_error) {
        numberOfFailed++;
      } else if (action.applied) {
        numberOfPassed++;
      } else {
        numberOfSkipped++;
      }
    }
  }

  return (
    <Wrapper>
      {error && <Banner style={{ marginBottom: '16px' }} variant="critical" title={error} />}

      <div style={{ flex: 1, position: 'relative', overflowY: 'scroll', paddingBottom: '16px' }}>
        {isRunning && <Spinner style={{ height: '100%', display: 'flex', justifyContent: 'center' }} />}

        {!isRunning && !steps.length && !error && !result?.run_logs?.custom_booking_methods && (
          <Banner
            title={t('bookingRules.edit.test-runner.no-match', {
              method: `'${result?.input_shipment?.shipping_method}'`,
            })}
            style={{ marginBottom: '16px' }}
            variant="warning"
          />
        )}

        {!isRunning && !steps.length && !error && result?.run_logs?.custom_booking_methods && (
          <Banner
            title={t('bookingRules.edit.test-runner.no-step-output', {
              method: `'${result.input_shipment?.shipping_method}'`,
            })}
            style={{ marginBottom: '16px' }}
            variant="warning"
          />
        )}

        {steps.length > 0 && (
          <ListLayout.Stack>
            {steps.map((step, si) => (
              <Step step={step} key={`step-${si}`} />
            ))}
          </ListLayout.Stack>
        )}
      </div>

      <Footer>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <SimpleSelect
            renderOptionLabel={([value, label]) => (
              <DotBadge variant={runs[Number(value)].errors ? 'critical' : 'success'}>{label}</DotBadge>
            )}
            options={runs.map((_, index) => [
              index.toString(),
              t('bookingRules.edit.test-shipment-option', { id: index + 1 }),
            ])}
            onValueChange={([value]) => setSelected(value)}
            value={[selected]}
          />

          {steps.length > 0 && (
            <div style={{ display: 'flex', gap: '16px' }}>
              <DotBadge variant="success" style={{ whiteSpace: 'nowrap' }}>
                {numberOfPassed} {t('common.passed')}
              </DotBadge>
              <DotBadge variant="critical" style={{ whiteSpace: 'nowrap' }}>
                {numberOfFailed} {t('common.failed')}
              </DotBadge>
              <DotBadge variant="neutral" style={{ whiteSpace: 'nowrap' }}>
                {numberOfSkipped} {t('common.skipped')}
              </DotBadge>
            </div>
          )}
        </div>

        <RunTestButton i18nKey="bookingRules.edit.re-run-test" />
      </Footer>
    </Wrapper>
  );
}
